import { ReactComponent as EditIcon } from 'assets/icons/edit-dark.svg';
import { ReactComponent as ArrowDownIcon } from 'assets/icons/south.svg';
import { ReactComponent as ArrowUpIcon } from 'assets/icons/north.svg';
import { FIELD_TYPES } from 'constants/fieldTypes';
import {
  REMOVAL_REASON_USED_BY,
  SUCCESSION_SETTINGS_FIELD_NAMES,
} from 'constants/succession';
import { isArray, normalizeListOrder, reorder } from 'utility/helpers';
import {
  forbiddenCharacters,
  isEmpty,
  validateLength,
} from 'utility/validation';

const LABEL_MAX_LENGTH = 128;

const { READINESS, CRITICALITY, REMOVAL_REASONS, JOB_FAMILIES } =
  SUCCESSION_SETTINGS_FIELD_NAMES;

const SYSTEM_AND_USERS_VALUES = new Set([
  REMOVAL_REASON_USED_BY.SYSTEM_AND_USERS,
  'system+users',
  'system + users',
  'both',
]);

const labelValidators = [
  {
    type: 'required',
    validator: isEmpty,
  },
  {
    type: 'forbiddenCharacters',
    validator: forbiddenCharacters,
  },
  {
    type: 'maxLength',
    validator: value => validateLength(value, 0, LABEL_MAX_LENGTH),
  },
];

export const COVERAGE_FIELD = {
  name: 'generic_coverage_ratio',
  translationKey: 'holders',
  type: FIELD_TYPES.NUMBER_STEPPER,
};

export const SETTINGS_DRAWER_TRANSLATION_KEYS = {
  [READINESS]: 'readiness',
  [CRITICALITY]: 'criticality',
  [REMOVAL_REASONS]: 'removalReasons',
  [JOB_FAMILIES]: 'jobFamilies',
};

const activeSwitchField = {
  name: 'is_active',
  translationKey: 'isActive',
  type: FIELD_TYPES.SWITCH,
  showStatusLabel: true,
};

export const prepareLabelFields = () => [
  {
    name: 'label',
    type: FIELD_TYPES.TEXT,
    required: true,
    validators: labelValidators,
  },
  activeSwitchField,
];

export const prepareNameFields = () => [
  {
    name: 'name',
    type: FIELD_TYPES.TEXT,
    required: true,
    validators: labelValidators,
  },
  activeSwitchField,
];

export const validateCoverageRatio = value =>
  isEmpty(value) && Number.isInteger(Number(value)) && Number(value) >= 1;

export const getActionColumn = labels => [
  {
    id: 'actions-column',
    title: labels.actions,
    rowKey: 'actions',
    align: 'right',
    rowCellClass: 'actions-cell',
    isHeaderAction: true,
    isSortingDisabled: true,
    minWidth: 72,
    maxWidth: 96,
  },
];

const textColumn = (id, title, rowKey, minWidth, maxWidth = '1fr') => ({
  id,
  title,
  rowKey,
  isSortingDisabled: true,
  minWidth,
  maxWidth,
});

export const getReadinessHeaders = labels => [
  textColumn(1, labels.name, 'label', 180),
  textColumn(2, labels.active, 'active', 100, 140),
];

export const getCriticalityHeaders = labels => [
  textColumn(1, labels.name, 'label', 180),
  textColumn(2, labels.active, 'active', 100, 140),
];

export const getRemovalReasonHeaders = labels => [
  textColumn(1, labels.name, 'label', 180),
  textColumn(2, labels.usedBy, 'usedBy', 140, 220),
  textColumn(3, labels.active, 'active', 100, 140),
];

export const getJobFamilyHeaders = labels => [
  textColumn(1, labels.name, 'name', 180),
  {
    ...textColumn(2, labels.jobsCount, 'jobsCount', 80, 120),
    align: 'right',
  },
  textColumn(3, labels.active, 'active', 100, 140),
];

export const sortByOrder = list =>
  [...(list || [])].sort(
    (left, right) => (Number(left.order) || 0) - (Number(right.order) || 0)
  );

const activeLabel = (isActive, labels) =>
  isActive ? labels.active : labels.inactive;

export const getRemovalReasonAudience = usedBy => {
  if (isArray(usedBy)) {
    const values = usedBy.map(value => String(value).toLowerCase());
    const hasSystem = values.includes(REMOVAL_REASON_USED_BY.SYSTEM);
    const hasUsers = values.includes(REMOVAL_REASON_USED_BY.USERS);

    if (hasSystem && hasUsers) {
      return REMOVAL_REASON_USED_BY.SYSTEM_AND_USERS;
    }
    if (hasSystem) return REMOVAL_REASON_USED_BY.SYSTEM;
    if (hasUsers) return REMOVAL_REASON_USED_BY.USERS;
    return null;
  }

  if (typeof usedBy !== 'string') return null;

  const normalized = usedBy.trim().toLowerCase();
  if (SYSTEM_AND_USERS_VALUES.has(normalized)) {
    return REMOVAL_REASON_USED_BY.SYSTEM_AND_USERS;
  }
  if (
    normalized === REMOVAL_REASON_USED_BY.SYSTEM ||
    normalized === REMOVAL_REASON_USED_BY.USERS
  ) {
    return normalized;
  }

  return null;
};

export const isUserRemovalReason = usedBy =>
  getRemovalReasonAudience(usedBy) === REMOVAL_REASON_USED_BY.USERS;

export const getRemovalReasonUsedByLabel = (usedBy, labels) => {
  const audience = getRemovalReasonAudience(usedBy);

  if (audience === REMOVAL_REASON_USED_BY.SYSTEM) return labels.system;
  if (audience === REMOVAL_REASON_USED_BY.USERS) return labels.users;
  if (audience === REMOVAL_REASON_USED_BY.SYSTEM_AND_USERS) {
    return labels.systemAndUsers;
  }
  if (typeof usedBy === 'string') return usedBy;

  return '';
};

export const prepareReadinessRows = (items, labels, renderActions) =>
  sortByOrder(items).map((item, index, ordered) => ({
    id: item.id,
    label: item.label,
    active: activeLabel(item.is_active, labels),
    actions: renderActions(item, index, ordered.length),
  }));

export const prepareCriticalityRows = (items, labels, renderActions) =>
  sortByOrder(items).map((item, index, ordered) => ({
    id: item.id,
    label: item.label,
    active: activeLabel(item.is_active, labels),
    actions: renderActions(item, index, ordered.length),
  }));

export const prepareRemovalReasonRows = (items, labels, renderActions) =>
  (items || []).map(item => ({
    id: item.id,
    label: item.label,
    usedBy: getRemovalReasonUsedByLabel(item.used_by, labels.usedBy),
    active: activeLabel(item.is_active, labels),
    actions: renderActions(item),
  }));

export const prepareJobFamilyRows = (items, labels, renderActions) =>
  (items || []).map(item => ({
    id: item.id,
    name: item.name,
    jobsCount: Number.isInteger(item.jobs_count) ? item.jobs_count : 0,
    active: activeLabel(item.is_active, labels),
    actions: renderActions(item),
  }));

export const prepareOrderedMenuItems = (
  labels,
  item,
  index,
  count,
  onEdit,
  onMoveUp,
  onMoveDown
) => [
  {
    type: 'edit',
    label: labels.edit,
    action: () => onEdit(item),
    icon: EditIcon,
    shouldItemRender: () => true,
  },
  {
    type: 'moveUp',
    label: labels.moveUp,
    action: () => onMoveUp(item),
    icon: ArrowUpIcon,
    shouldItemRender: () => index > 0,
  },
  {
    type: 'moveDown',
    label: labels.moveDown,
    action: () => onMoveDown(item),
    icon: ArrowDownIcon,
    shouldItemRender: () => index < count - 1,
  },
];

export const prepareEditMenuItems = (labels, onEdit) => [
  {
    type: 'edit',
    label: labels.edit,
    action: onEdit,
    icon: EditIcon,
    shouldItemRender: () => true,
  },
];

export const getDrawerInitialData = (listKey, item) => {
  const isFamily = listKey === JOB_FAMILIES;

  if (!item) {
    return isFamily
      ? { name: '', is_active: true }
      : { label: '', is_active: true };
  }

  if (isFamily) {
    return {
      id: item.id,
      name: item.name || '',
      is_active: Boolean(item.is_active),
    };
  }

  return {
    id: item.id,
    label: item.label || '',
    is_active: Boolean(item.is_active),
  };
};

const updateItemById = (list, itemId, changes) =>
  (list || []).map(item =>
    item.id === itemId ? { ...item, ...changes } : item
  );

const appendItem = (list, item) => [...(list || []), item];

const appendOrderedItem = (list, item) =>
  normalizeListOrder([...sortByOrder(list), item]);

export const moveOrderedItem = (list, itemId, direction) => {
  const ordered = sortByOrder(list);
  const index = ordered.findIndex(item => item.id === itemId);
  const targetIndex = direction === 'up' ? index - 1 : index + 1;

  if (index < 0 || targetIndex < 0 || targetIndex >= ordered.length) {
    return list;
  }

  return normalizeListOrder(reorder(ordered, index, targetIndex));
};

export const applyDrawerValues = (settings, listKey, item, values) => {
  const current = settings[listKey] || [];
  const isEdit = Boolean(item?.id);
  let nextList;

  if (listKey === JOB_FAMILIES) {
    const changes = { name: values.name, is_active: Boolean(values.is_active) };
    nextList = isEdit
      ? updateItemById(current, item.id, changes)
      : appendItem(current, changes);
  } else if (listKey === REMOVAL_REASONS) {
    const changes = {
      label: values.label,
      is_active: Boolean(values.is_active),
    };
    nextList = isEdit
      ? updateItemById(current, item.id, changes)
      : appendItem(current, {
          ...changes,
          is_system: false,
          used_by: REMOVAL_REASON_USED_BY.USERS,
        });
  } else {
    const changes = {
      label: values.label,
      is_active: Boolean(values.is_active),
    };
    if (isEdit) {
      nextList = updateItemById(current, item.id, changes);
    } else {
      nextList = appendOrderedItem(current, {
        ...changes,
        ...(listKey === READINESS ? { is_system: false } : {}),
      });
    }
  }

  return {
    ...settings,
    [listKey]: nextList,
  };
};
